// ApprovalDialog — modal hỏi user trước khi AI execute tool có risk.
// State quản lý từ ChatPanel: open + tool info + 2 callback resolve.

import { AlertTriangle, Check, X } from "lucide-solid";
import { type Component, Show } from "solid-js";
import type { ToolRisk } from "../../lib/agent-sdk";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export interface ApprovalDialogProps {
  open: boolean;
  toolName: string;
  args: unknown;
  risk: ToolRisk;
  onApprove: () => void;
  onReject: () => void;
}

const RISK_STYLES: Record<ToolRisk, { label: string; classes: string }> = {
  low: {
    label: "Low risk",
    classes: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30",
  },
  medium: {
    label: "Medium risk",
    classes: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30",
  },
  high: {
    label: "High risk",
    classes: "bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/30",
  },
};

export const ApprovalDialog: Component<ApprovalDialogProps> = (props) => {
  return (
    <Dialog
      open={props.open}
      onOpenChange={(o) => {
        // Close X / overlay click / ESC → coi là reject
        if (!o) props.onReject();
      }}
    >
      <DialogContent class="max-w-md">
        <DialogHeader>
          <DialogTitle class="flex items-center gap-2">
            <AlertTriangle class="h-4 w-4 text-amber-500" />
            AI muốn chạy tool này
          </DialogTitle>
          <DialogDescription>
            Review args trước khi execute. Tool này có thể thay đổi dữ liệu.
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-3">
          <div class="flex items-center gap-2">
            <code class="text-sm font-mono bg-muted px-2 py-1 rounded">{props.toolName}</code>
            <span class={`text-xs px-2 py-0.5 rounded-full ${RISK_STYLES[props.risk].classes}`}>
              {RISK_STYLES[props.risk].label}
            </span>
          </div>

          <div>
            <div class="text-xs text-muted-foreground mb-1">Arguments:</div>
            <pre class="text-xs bg-muted/50 border rounded p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">
              {formatArgs(props.args)}
            </pre>
          </div>

          <Show when={containsPlaceholder(props.args)}>
            <div class="text-xs flex items-start gap-1.5 p-2 rounded border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
              <AlertTriangle class="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                Có giá trị nhìn giống placeholder (vd "ID của ..."). AI có thể đang đoán — verify trước khi approve.
              </span>
            </div>
          </Show>
        </div>

        <DialogFooter class="gap-2">
          <Button variant="outline" onClick={props.onReject}>
            <X class="h-4 w-4 mr-1" />
            Reject
          </Button>
          <Button onClick={props.onApprove}>
            <Check class="h-4 w-4 mr-1" />
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function formatArgs(args: unknown): string {
  try {
    return JSON.stringify(args, null, 2);
  } catch {
    return String(args);
  }
}

// Detect AI placeholder strings như "ID của group ABC", "<id>", "TODO", ...
function containsPlaceholder(args: unknown): boolean {
  const s = formatArgs(args);
  return /\b(ID c[ủu]a|<[^>]+>|TODO|FIXME|placeholder|to[\s_-]?be[\s_-]?determined|TBD|<id>)\b/i.test(s);
}
